import r2rServicesClient from 'common/utils/r2rServicesClient'
import { indexOf } from 'common/utils/arrayUtils'
import { schedulesConfigEndpoint } from 'common/utils/paths'

// Object for storing promises.
// Schema:
// {
//   myEndpointName: [{
//     id: <see createRequestId()>
//     promise: A Promise
//   }]
// }
//
// Note that REQUEST[endpointName] is intended to be a queue of promises, so
// REQUEST[endpointName][0] is the oldest request to 'endpointName'.
const REQUESTS = {}
// Max number of promises stored for each endpoint
const REQUEST_LIMIT = 5

const createRequestId = (params, headers) => {
  return { headers, params }
}

const getPromise = (endpoint, id) => {
  const index = findPromiseIndex(endpoint, id)
  if (index < 0) {
    return null
  }

  return REQUESTS[endpoint][index].promise
}

const findPromiseIndex = (endpoint, id) => {
  const endpointRequests = REQUESTS[endpoint]
  if (!endpointRequests) {
    // Endpoint has no cached promises
    return -1
  }

  return indexOf(endpointRequests, (p) => compareRequestIds(p.id, id))
}

const addPromise = (endpoint, promise, id) => {
  let endpointRequests = REQUESTS[endpoint]
  if (!endpointRequests) {
    // This is the first request to this endpoint, create the endpoint queue
    endpointRequests = []
    REQUESTS[endpoint] = endpointRequests
  }

  if (endpointRequests.length >= REQUEST_LIMIT) {
    // Remove the oldest promise. Note this is O(n)
    // The limit is put in place to avoid memory leaks by allowing the garbage collection to
    // clean old promises
    endpointRequests.shift()
  }

  // Add new promise to the end of the queue
  endpointRequests.push({ id, promise })
}

const removePromise = (endpoint, id) => {
  const index = findPromiseIndex(endpoint, id)
  if (index < 0) {
    return
  }

  REQUESTS[endpoint].splice(index, 1)
}

// Removes errored promises from our cache so the error isn't cached and then rethrow the error
const onErrorCallback = (endpoint, id) => (error) => {
  removePromise(endpoint, id)
  throw error
}

// Note: This is dependant on implementation of createRequestId()
const compareRequestIds = (i1, i2) => (
  shallowCompare(i1.headers, i2.headers) && shallowCompare(i1.params, i2.params)
)

const shallowCompare = (obj1, obj2) => (
  Object.keys(obj1).reduce((acc, key) => acc && (key in obj2) && obj1[key] == obj2[key], true) &&
  Object.keys(obj2).reduce((acc, key) => acc && (key in obj1) && obj2[key] == obj1[key], true)
)

/* This Manager providers a request-caching wrapper for sending HTTP requests.
 * Requests are cached based on what endpoint they are sent to and the params/headers that are
 * sent to them.
 *
 * Requests that throw an error will be removed from the cache, so successive requests don't all
 * receive an error (in case the error was a one-off)
 *
 * Note that param and header keys are case sensitive (even though HTTP does not consider case)
 */
class Manager {
  /** Send a HTTP GET request.
   * Set config.skipCache to avoid caching the request
   *
   * @param {string} endpoint Target endpoint location
   * @param {Object} config Contains optional 'params' and 'header' objects.
   *
   * @returns {Promise} A new or existing promise for the request
   */
  get = (endpoint, config = {}) => {
    const params = config.params || {}
    const headers = config.header || {}

    if (!config.skipCache) {
      const id = createRequestId(params, headers)

      // Return existing promise if possible
      const existingPromise = getPromise(endpoint, id)
      if (existingPromise) {
        return existingPromise
      }

      // Send request and store promise
      const promise = r2rServicesClient
        .get(endpoint, { params, headers })
        .catch(onErrorCallback(endpoint, id))

      addPromise(endpoint, promise, id)
      return promise
    }

    return r2rServicesClient.get(endpoint, { params, headers })
  }

  /** Send a v1.5 SchedulesConfig request.
   *
   * @param {string} origin Origin location
   * @param {string} destination Destination location
   * @param {Object} config Additional params. See ApiManager.Get()
   *
   * @returns {Promise} A new or existing promise for the request
  */
  schedulesConfig = (origin, destination, config = {}) => {
    const params = {
      ...config.params,
      key: '86f5qBa1',
      oName: origin,
      dName: destination,
    }
    return this.get(schedulesConfigEndpoint, { ...config, params })
  }
}

const apiManager = new Manager()
export default apiManager
